/**
 * Module: TasksScreen
 * Role: Buyer task list. Fetches GET /api/v2/buyer/tasks and renders:
 *   - isPending  → Skeleton grid (2×3)
 *   - isError    → honest EmptyState (no ₽ rendered — EC-3)
 *   - empty list → honest EmptyState with bot deep-link copy (EC-4)
 *   - list       → 2-col prototype card grid with ProductImg, title clamp,
 *                  terracotta Caveat cashback, marketplace badge, slots badge (EC-5)
 *   - row tap    → navigate('/product/' + offer_id) (FTR-1002 Task 5)
 *   FTR-1106 Task 4: banner «Разблокируй премиум-задания» for standard buyers.
 *
 * Visual: matches prototype.jsx HomeScreen витрина grid (2-col, Card p=0, image h=170,
 * overlay badges, Caveat "+X ₽ вернётся").
 *
 * Inline interfaces — per WalletCard precedent; never import from api/types.ts (L-240).
 * D-1: TaskItem has NO `status` field. Render response.items only; ignore `gated`.
 * D-2 (updated FTR-1002 Task 5): row tap navigates to ProductScreen overlay.
 *
 * Uses:
 *   - components/atoms: IOSScreen, PageHeader, Card, ProductImg, EmptyState, Skeleton, Ic, GlassPill
 *   - @tanstack/react-query: useQuery
 *   - api/client: apiFetch
 *   - react-router-dom: useNavigate
 *   - styles/tokens: P, S, T
 *
 * Used by:
 *   - screens/AppShell (via react-router-dom Route at /tasks)
 */
import { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import { fetchTasksPage, getNextTasksPageParam, type TaskItem, type TasksResponse } from '../api/tasks';
import {
  IOSScreen,
  PageHeader,
  Card,
  ProductImg,
  EmptyState,
  Skeleton,
  Ic,
  GlassPill,
} from '../components/atoms';
import { P, S, T } from '../styles/tokens';

// ─── Inline Types (L-240 — never import from api/types.ts) ───────────────────

// FTR-1106 Task 4: minimal profile slice for standard-buyer banner gate.
// queryKey ['buyer','profile'] matches ProfileScreen — shared cache, no extra request.
interface ProfileCreatorSlice {
  account_kind?: 'standard' | 'creator';
  creator_status?: string | null;
}

// Types imported from ../api/tasks (BUG-1164 — SSOT)

// ─── Sub-components ──────────────────────────────────────────────────────────

interface TaskCardProps {
  item: TaskItem;
  onTap: (offerId: string) => void;
}

function TaskCard({ item, onTap }: TaskCardProps) {
  const hue = item.product_title.charCodeAt(0) % 360;
  const cashbackRubles = Math.round(item.cashback_kopecks / 100);
  const priceRubles = item.product_price_kopecks
    ? Math.round(item.product_price_kopecks / 100)
    : null;
  const discountPct =
    priceRubles && cashbackRubles
      ? Math.round((cashbackRubles / priceRubles) * 100)
      : null;

  return (
    <Card
      p={0}
      r={16}
      hover
      onClick={() => onTap(item.offer_id)}
      style={{ overflow: 'hidden', cursor: 'pointer' }}
    >
      {/* ── Image area with overlaid badges ── */}
      <div style={{ position: 'relative' }}>
        {item.product_image_url ? (
          <img
            src={item.product_image_url}
            alt={item.product_title}
            style={{
              width: '100%',
              height: 170,
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <ProductImg
            h={170}
            w="100%"
            r={0}
            hue={hue}
          />
        )}

        {/* Marketplace badge — top-left white pill */}
        <span
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            background: 'rgba(255,255,255,0.92)',
            color: S.label,
            padding: '3px 8px',
            borderRadius: 6,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 0.1,
            lineHeight: 1.4,
          }}
        >
          {item.marketplace}
        </span>

        {/* Slots badge — top-right terracotta pill with flame icon (shown when >0 slots) */}
        {item.slots_available > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: P.terracotta,
              color: '#fff',
              padding: '3px 7px',
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <Ic name="flame" size={10} color="#fff" />
            {item.slots_available}
          </span>
        )}
      </div>

      {/* ── Card body ── */}
      <div style={{ padding: '10px 12px 12px' }}>
        {/* Title — 2-line clamp, subheadline 600 per prototype */}
        <div
          style={{
            font: T.subhead,
            fontWeight: 600,
            color: S.label,
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: 36,
          } as CSSProperties}
        >
          {item.product_title}
        </div>

        {/* Price + discount badge row (shown when price is available) */}
        {priceRubles !== null && (
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 6,
              marginTop: 8,
            }}
          >
            <span
              style={{
                font: T.subhead,
                fontWeight: 700,
                color: S.label,
              }}
            >
              {priceRubles.toLocaleString('ru-RU')}&nbsp;₽
            </span>
            {discountPct !== null && discountPct > 0 && discountPct < 100 && (
              <span
                style={{
                  font: T.label,
                  fontWeight: 700,
                  color: P.success,
                  background: 'rgba(45,138,78,0.12)',
                  padding: '1px 5px',
                  borderRadius: 4,
                }}
              >
                −{discountPct}%
              </span>
            )}
          </div>
        )}

        {/* Caveat cashback + "вернётся" annotation — inline per prototype */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 4,
            marginTop: priceRubles !== null ? 4 : 8,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontFamily: T.fontAccent,
              fontSize: 19,
              color: P.terraDark,
              fontWeight: 700,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            +{cashbackRubles}&nbsp;₽
          </span>
          <span
            style={{
              font: T.captionAlt,
              color: S.secondaryLabel,
            }}
          >
            вернётся
          </span>
        </div>
      </div>
    </Card>
  );
}

// ─── CreatorBanner (FTR-1106 Task 4) ─────────────────────────────────────────
// Shown for standard buyers as motivation to unlock premium tasks.
// ADR-022 §7: navigate() — НИКАКОГО openExternal(BOT_LINK).
interface CreatorBannerProps {
  onTap: () => void;
}

function CreatorBanner({ onTap }: CreatorBannerProps) {
  return (
    <Card
      hover
      onClick={onTap}
      style={{
        cursor: 'pointer',
        background: `linear-gradient(135deg, ${P.terracotta} 0%, ${P.terraDark} 100%)`,
        color: '#fff',
        gridColumn: '1 / -1',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <p style={{ font: T.headline, color: '#ffffff', margin: '0 0 3px' }}>
            Разблокируй премиум-задания
          </p>
          <p style={{ font: T.footnote, color: 'rgba(255,255,255,0.85)', margin: 0 }}>
            Пройди верификацию — стань креатором
          </p>
        </div>
        <GlassPill variant="light" size={32} style={{ padding: '0 10px', flexShrink: 0 }}>
          <Ic name="chevR" size={12} />
        </GlassPill>
      </div>
    </Card>
  );
}

// ─── Skeleton grid ────────────────────────────────────────────────────────────

function TasksGridSkeleton() {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <div key={i}>
          <Skeleton h={170} r={16} />
          <Skeleton h={14} w="80%" style={{ marginTop: 10 }} />
          <Skeleton h={12} w="55%" style={{ marginTop: 6 }} />
        </div>
      ))}
    </>
  );
}

// ─── TasksScreen ─────────────────────────────────────────────────────────────

export default function TasksScreen() {
  const navigate = useNavigate();

  const {
    data,
    isPending,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<TasksResponse>({
    queryKey: ['buyer', 'tasks'],
    queryFn: ({ pageParam }) => fetchTasksPage(pageParam as number),
    initialPageParam: 0,
    getNextPageParam: getNextTasksPageParam,
    retry: false,
  });

  // FTR-1106 Task 4: profile query for banner gate (shared cache with ProfileScreen).
  const { data: profile } = useQuery<ProfileCreatorSlice>({
    queryKey: ['buyer', 'profile'],
    queryFn: () => apiFetch<ProfileCreatorSlice>('/api/v2/buyer/profile'),
    retry: false,
  });

  // Show banner for standard buyers (no creator role yet).
  const isStandard = profile?.account_kind === 'standard' || (!profile?.account_kind && profile !== undefined);

  // BUG-1164: flatten all pages into a single items array for rendering.
  const allItems = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <IOSScreen tabBarSpace>
      <PageHeader title="Задания" />

      <div
        style={{
          padding: '0 12px 24px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 10,
        }}
      >
        {/* FTR-1106 Task 4: creator banner — full-width, motivate standard buyers */}
        {isStandard && (
          <CreatorBanner onTap={() => navigate('/creator-onboarding/promo')} />
        )}

        {/* Loading state — 2-col skeleton grid */}
        {isPending && <TasksGridSkeleton />}

        {/* Error state — EC-3: no ₽ value in DOM */}
        {isError && !isPending && (
          <div style={{ gridColumn: '1 / -1' }}>
            <EmptyState
              title="Не удалось загрузить задания"
              subtitle="Попробуй позже"
            />
          </div>
        )}

        {/* Empty list state — EC-4 */}
        {!isPending && !isError && allItems.length === 0 && (
          <div style={{ gridColumn: '1 / -1' }}>
            <EmptyState
              title="Заданий пока нет"
              subtitle="Возьми задание в боте — оно появится здесь"
            />
          </div>
        )}

        {/* Task grid — EC-5 */}
        {!isPending && !isError && allItems.length > 0 &&
          allItems.map((item) => (
            <TaskCard
              key={item.offer_id}
              item={item}
              onTap={(offerId) => navigate('/product/' + offerId)}
            />
          ))
        }

        {/* BUG-1164: "Load more" pill — visible while more pages exist */}
        {hasNextPage && !isFetchingNextPage && (
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
            <GlassPill onClick={() => { fetchNextPage(); }}>Показать ещё</GlassPill>
          </div>
        )}
        {isFetchingNextPage && (
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
            <GlassPill>Загрузка...</GlassPill>
          </div>
        )}
      </div>
    </IOSScreen>
  );
}
